import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import useStore from 'state/knovStore'
import { isMobile } from 'lib/device'
import { getCenterIndex } from 'state/imperativeApis/swiperApi'
import { BIG, SMALL } from 'lib/transform'

export default function useAnimatedSlide() {
    const animationStateRef = useRef({
        isAnimating: false,
        pendingCompletion: false,
        rafId: null as number | null,
        timeoutId: null as ReturnType<typeof setTimeout> | null,
    })

    // Get animation state from store
    const {
        animatedPanelInsert,
        completeAnimatedInsert,
        animatedPanelRemove,
        completeAnimatedRemove,
    } = useStore.getState().panels

    // Memoize the completion handler
    const handleAnimationComplete = useCallback(
        (isInsert: boolean) => {
            if (animationStateRef.current.pendingCompletion) return

            animationStateRef.current.pendingCompletion = true

            // Use RAF to batch the completion
            requestAnimationFrame(() => {
                if (isInsert) {
                    completeAnimatedInsert()
                } else {
                    completeAnimatedRemove()
                }
                animationStateRef.current.isAnimating = false
                animationStateRef.current.pendingCompletion = false
            })
        },
        [completeAnimatedInsert, completeAnimatedRemove],
    )

    // Calculate transform styles based on animation state
    const getTransformStyle = useCallback(
        (index: number, _centerIndexOverride?: number): React.CSSProperties => {
            if (!animatedPanelInsert && !animatedPanelRemove) return {}

            // Start animation tracking
            if (!animationStateRef.current.isAnimating) {
                animationStateRef.current.isAnimating = true
            }

            const centerIndex = getCenterIndex()
            const rightTranslate = 'translateX(100%)'
            const leftTranslate = 'translateX(-100%)'
            let scale
            let translate

            const hideStyles = {
                display: 'none',
            }

            if (animatedPanelInsert) {
                const openCenter = animatedPanelInsert?.actionParams?.openCenter
                if (animatedPanelInsert.direction === 'right') {
                    if (index < animatedPanelInsert.index) return {}
                    translate = rightTranslate
                    // Keep sizes correct during the shift-right animation:
                    // - Left (becoming center): BIG
                    // - Center (becoming right): SMALL
                    // - Right: ensure SMALL so it doesn't flash at scale(1)
                    if (index === centerIndex - 1) scale = BIG
                    else if (index === centerIndex || index === centerIndex + 1) scale = SMALL
                } else if (animatedPanelInsert.direction === 'left') {
                    // For openCenter, only shift left the panels up to insertIndex (left + center);
                    // keep the right panel visually fixed (no translate).
                    if (openCenter) {
                        if (index > animatedPanelInsert.index) return {}
                    } else {
                        if (index >= animatedPanelInsert.index) return {}
                    }
                    translate = leftTranslate
                    // For openCenter, smoothly shrink the old center as it slides left.
                    if (openCenter) {
                        if (index === centerIndex) scale = SMALL
                    } else {
                        if (index === centerIndex + 1) scale = BIG
                        else if (index === centerIndex) scale = SMALL
                    }
                }
            }

            if (animatedPanelRemove) {
                if (animatedPanelRemove.direction === 'right') {
                    if (index > animatedPanelRemove.index) return {}
                    else if (index === animatedPanelRemove.index) return hideStyles
                    else {
                        translate = rightTranslate
                        if (index === centerIndex - 1) scale = BIG
                        else if (index === centerIndex) scale = SMALL
                    }
                } else if (animatedPanelRemove.direction === 'left') {
                    if (index < animatedPanelRemove.index) return {}
                    else if (index === animatedPanelRemove.index) return hideStyles
                    else {
                        translate = leftTranslate
                        if (index === centerIndex + 1) scale = BIG
                        else if (index === centerIndex) scale = SMALL
                    }
                }
            }

            return {
                transform: scale ? `${translate} scale(${scale})` : translate,
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'transform',
            }
        },
        [animatedPanelInsert, animatedPanelRemove],
    )

    // Listen for transition end to complete animation
    useEffect(() => {
        if (!animatedPanelInsert && !animatedPanelRemove) {
            // Reset animation state when no animation is running
            animationStateRef.current.isAnimating = false
            animationStateRef.current.pendingCompletion = false
            if (animationStateRef.current.timeoutId) {
                clearTimeout(animationStateRef.current.timeoutId)
                animationStateRef.current.timeoutId = null
            }
            return
        }

        const scheduleCompletion = (force = false) => {
            // Schedule completion for next frame to avoid forced reflow
            animationStateRef.current.rafId = requestAnimationFrame(() => {
                // Batch reads
                const isAnimating = animationStateRef.current.isAnimating
                const isPending = animationStateRef.current.pendingCompletion

                if ((!isAnimating && !force) || isPending) return

                // Batch writes
                handleAnimationComplete(!!animatedPanelInsert)
            })
        }

        const handleTransitionEnd = (e: TransitionEvent) => {
            if (e.propertyName === 'transform') scheduleCompletion()
        }

        document.addEventListener('transitionend', handleTransitionEnd)
        animationStateRef.current.timeoutId = setTimeout(() => scheduleCompletion(true), 380)

        return () => {
            document.removeEventListener('transitionend', handleTransitionEnd)
            if (animationStateRef.current.rafId) {
                cancelAnimationFrame(animationStateRef.current.rafId)
                animationStateRef.current.rafId = null
            }
            if (animationStateRef.current.timeoutId) {
                clearTimeout(animationStateRef.current.timeoutId)
                animationStateRef.current.timeoutId = null
            }
        }
    }, [animatedPanelInsert, animatedPanelRemove, handleAnimationComplete])

    return {
        getTransformStyle,
    }
}
